import { Placement } from '@floating-ui/react';
import { format } from 'date-fns';
import { useState } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Dropdown } from '../DateRangeDropdown/components';

type SingleDateDropdownProps = {
  date?: Date;
  className?: string;
  onChange: (date?: Date) => void;
  isDisabled?: boolean;
  placement?: Placement;
  containerClassName?: string;
  placeholder?: string;
};

export const SingleDateDropdown = ({
  date,
  className,
  onChange,
  isDisabled,
  placement,
  placeholder,
  containerClassName,
}: SingleDateDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const weeklyDays = t('date_picker.weekly_days', {
    returnObjects: true,
  }) as string[];

  const monthNames = t('date_picker.month_names', {
    returnObjects: true,
  }) as string[];

  return (
    <div
      className={twMerge(
        'flex items-center space-x-2 text-nowrap rounded border-2 border-transparent',
        isOpen && 'border-blueNuit',
        containerClassName,
      )}
    >
      <Dropdown
        isOpen={isOpen}
        hiddenClassName="border-t p-2 rounded-md z-[60]"
        buttonClassMame={twMerge(
          'border-white focus:border-white active:border-white rounded-md h-full',
        )}
        containerClassName={className}
        placement={placement}
        setIsOpen={setIsOpen}
        disabled={isDisabled}
        placeholder={date ? format(date, 'dd/MM/yyyy') : placeholder}
        onOutsideClick={() => setIsOpen(false)}
      >
        <div className="z-60 flex flex-col gap-2 p-1">
          <DayPicker
            mode="single"
            classNames={{
              button_previous: 'size-5',
              button_next: 'size-5',
              chevron: 'size-5',
              caption_label: 'text-sm font-black self-center',
              day: 'size-5 text-xs font-black text-primaryBlue border-transparent border data-[today=true]:border-blueNuit data-[selected=true]:rounded-full',
              day_button: 'size-5 text-xs',
              month_caption: 'flex h-5',
              nav: `${getDefaultClassNames().nav} h-5`,
              selected: 'font-black bg-blueNuit text-white',
              weeks: `${getDefaultClassNames().weeks} text-xs`,
              month_grid: `${getDefaultClassNames().month_grid} border-separate border-spacing-1`,
              today: 'text-primaryBlue rounded-sm',
            }}
            defaultMonth={date ?? new Date()}
            selected={date}
            onSelect={(date) => {
              onChange(date);
              setIsOpen(false);
            }}
            ISOWeek
            showOutsideDays
            formatters={{
              formatCaption(date) {
                return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
              },
              formatWeekdayName: (weekday) => {
                return weeklyDays[weekday.getDay()];
              },
            }}
          />
        </div>
      </Dropdown>
    </div>
  );
};
